import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import apiClient from '../../api/apiClient';
import { toastError } from '../common/toast';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';

const ViewClosedComplaints = ({ loadData = true }) => {
  const navigate = useNavigate();
  
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (loadData) {
      fetchClosedComplaints();
    }
  }, [loadData]);

  const fetchClosedComplaints = async () => {
    setLoading(true);
    try {
      // Fetch complaints with status filter for closed only
      const response = await apiClient.get('/complaints?status=closed');
      setComplaints(response.data || []);
    } catch (error) {
      console.error('Error fetching closed complaints:', error);
      toastError('Failed to fetch complaints history');
      setComplaints([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClass = status.toLowerCase().replace(/_/g, '-');
    return (
      <span className={`complaint-status status-${statusClass}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    return (
      <span className={`complaint-priority priority-${priority.toLowerCase()}`}>
        {priority}
      </span>
    );
  };

  const columns = [
    {
      Header: 'Complaint ID',
      accessor: 'complaint_id',
      Cell: ({ value }) => `#${value}`
    },
    {
      Header: 'Date Created',
      accessor: 'created_at',
      Cell: ({ value }) => formatDateToDDMMYYYY(value)
    },
    {
      Header: 'Date Resolved',
      accessor: 'resolved_at',
      Cell: ({ value }) => value ? formatDateToDDMMYYYY(value) : 'N/A'
    },
    {
      Header: 'PG Name',
      accessor: 'pg_name',
      Cell: ({ value, row }) => (
        <RTButton
          type="link"
          onClick={() => window.open(`/paying-guest/view/${row.pg_id}`, '_blank')}
          className="rt-link-button"
        >
          {value}
        </RTButton>
      )
    },
    {
      Header: 'Building',
      accessor: 'building_name',
      Cell: ({ value, row }) => (
        <RTButton
          type="link"
          onClick={() => window.open(`/building/view/${row.building_id}`, '_blank')}
          className="rt-link-button"
        >
          {value}
        </RTButton>
      )
    },
    {
      Header: 'Type',
      accessor: 'complaint_type',
      Cell: ({ value }) => {
        // Handle both string and object types
        if (typeof value === 'object' && value !== null) {
          return value.type_name || value.name || 'Unknown';
        }
        return value || 'Unknown';
      }
    },
    {
      Header: 'Priority',
      accessor: 'priority',
      Cell: ({ value }) => getPriorityBadge(value)
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }) => getStatusBadge(value)
    },
    {
      Header: 'Assigned To',
      accessor: 'assigned_to_name',
      Cell: ({ value }) => value || 'Unassigned'
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <RTButton
            type="link"
            onClick={() => navigate(`/complaints/view/${row.complaint_id}`)}
            style={{ fontWeight: 'bold' }}
          >
            View Details
          </RTButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <RTSpinner size="medium" showMessage={true} message="Loading complaints history..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 0' }}>
      {!loadData ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#666',
          fontSize: '16px'
        }}>
          Click on this tab to load complaints history
        </div>
      ) : (
        <Table
          columns={columns}
          data={complaints}
          loading={loading}
          exportable={true}
          showSearch={true}
          showPagination={true}
          emptyMessage="No complaints history found"
        />
      )}
    </div>
  );
};

export default ViewClosedComplaints;
